import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaTrash, FaSearch, FaPlus, FaGripVertical } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import LoadingSpinner from '../../components/LoadingSpinner';
import { deleteCategory, updateCategoryOrder } from '../../store/actions';
import useTitle from '../../hooks/useTitle';
import './AdminCategories.css';

const AdminCategories = () => {
    const { data: categories, loading, error } = useSelector(state => state.categories.all);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [deleteWithProducts, setDeleteWithProducts] = useState(false);
    const [orderedCategories, setOrderedCategories] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    // Задаем название сайта
    useTitle('Категории продукции | Админ-панель');

    useEffect(() => {
        if (categories && categories.length > 0) {
            setOrderedCategories([...categories]);
        }
    }, [categories]);

    const handleEditCategory = (categoryId) => {
        navigate(`/performance-admin-panel/categories/${categoryId}`);
    };

    const handleAddCategory = () => {
        navigate('/performance-admin-panel/categories/new');
    };

    const handleDeleteCategory = (category, e) => {
        e.stopPropagation(); // Предотвращаем переход к редактированию при клике на иконку удаления
        setCategoryToDelete(category);
        setShowDeleteModal(true);
        setDeleteWithProducts(false);
    };

    const confirmDelete = () => {
        if (categoryToDelete) {
            dispatch(deleteCategory(categoryToDelete.id, deleteWithProducts));
            setShowDeleteModal(false);
            setCategoryToDelete(null);
            setDeleteWithProducts(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setCategoryToDelete(null);
    };

    
    // Проверка, активен ли поиск
    const isSearchActive = searchTerm.trim() !== '';


    const handleDragStart = () => {
        if (isSearchActive) return; // Блокируем перетаскивание при активном поиске
        setIsDragging(true);
    };

    const handleDragEnd = (result) => {
        setIsDragging(false);
        if (isSearchActive || !result.destination) return;
        
        const items = Array.from(orderedCategories);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        setOrderedCategories(items);
        
        // Сохраняем новый порядок на сервере
        const orderData = items.map((category, index) => ({
            id: category.id,
            displayOrder: index
        }));
        
        dispatch(updateCategoryOrder(orderData));
    };

    // Фильтрация категорий по поисковому запросу
    const filteredCategories = orderedCategories.filter(category => 
        category.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Категории продукции</h2>
                <Button 
                    variant="primary" 
                    className="add-category-btn d-flex align-items-center"
                    onClick={handleAddCategory}
                >
                    <FaPlus className="me-sm-2" /> 
                    <span className="d-none d-sm-inline">Добавить</span>
                </Button>
            </div>

            <Form.Group className="mb-4 position-relative">
                <Form.Control
                    placeholder="Поиск по категориям..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: '2.5rem' }}
                />
                <div className="position-absolute" style={{ top: '0', bottom: '0', left: '15px', display: 'flex', alignItems: 'center' }}>
                    <FaSearch className="text-muted" />
                </div>
            </Form.Group>

            {loading ? (
                <div className="spinner-container">
                    <LoadingSpinner message="Загрузка категорий..." />
                </div>
            ) : error ? (
                <div className="alert alert-danger">
                    {error}
                </div>
            ) : categories && categories.length > 0 ? (
                <div className="admin-categories-container">
                    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                        <Droppable droppableId="categories">
                            {(provided) => (
                                <div 
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className="categories-droppable"
                                >
                                    {filteredCategories.map((category, index) => (
                                        <Draggable 
                                            key={category.id}
                                            draggableId={category.id.toString()}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className={`admin-category-card ${snapshot.isDragging ? 'dragging' : ''} ${isSearchActive ? 'search-active' : ''}`}
                                                    onMouseEnter={() => !isDragging && setHoveredCategory(category.id)}
                                                    onMouseLeave={() => !isDragging && setHoveredCategory(null)}
                                                    onClick={() => !isDragging && handleEditCategory(category.id)}
                                                >
                                                    <div className="admin-category-content">
                                                        <div 
                                                            {...(!isSearchActive ? provided.dragHandleProps : {})}
                                                            className="drag-handle"
                                                            style={{ opacity: isSearchActive ? 0.3 : 1 }}
                                                        >
                                                            <FaGripVertical />
                                                        </div>
                                                        <div 
                                                            className="admin-category-name" 
                                                            // onClick={() => !isDragging && handleEditCategory(category.id)}
                                                        >
                                                            {category.title}
                                                        </div>
                                                        <div className="admin-category-actions">
                                                            <FaTrash
                                                                className="admin-delete-icon"
                                                                onClick={(e) => handleDeleteCategory(category, e)}
                                                                title="Удалить категорию"
                                                                style={{ 
                                                                    visibility: hoveredCategory === category.id && !isDragging ? 'visible' : 'hidden'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            ) : (
                <div className="text-center py-5">
                    <p className="mb-3">Категории продукции отсутствуют</p>
                    <Button variant="outline-primary" onClick={handleAddCategory}>
                        <FaPlus className="me-2" /> Добавить категорию
                    </Button>
                </div>
            )}

            {/* Модальное окно подтверждения удаления */}
            <Modal show={showDeleteModal} onHide={cancelDelete} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Подтверждение удаления</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {categoryToDelete && (
                        <>
                            <p>Вы действительно хотите удалить категорию «{categoryToDelete.title}»?</p>
                            <Form.Check 
                                type="checkbox"
                                label="Удалить вместе со всеми связанными товарами"
                                checked={deleteWithProducts}
                                onChange={(e) => setDeleteWithProducts(e.target.checked)}
                            />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancelDelete}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        Удалить
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminCategories;